import { useRef, useState } from "react";
import { Alert } from "@mui/material";
import { useAuth } from "@bw/shared/hooks";
import type { PostResponse } from "@bw/shared/types/generated";
import { useLikePost } from "../hooks/useLikePost";
import { PostActionBar } from "./PostActionBar";
import { PostShareDialog } from "./PostShareDialog";
import { GuardianAwareError } from "../../../common/guardian/GuardianAwareError";

/**
 * The SMART action-bar wrapper — the ONE affordance every feed surface drops into
 * a PostCard's `actions` slot: `<PostCard post={post} actions={<PostActions post={post} />} />`.
 * It owns the like mutation, the auth gate, and the share sheet's open state so each
 * surface (network Feed, Home, community Feed) stays a one-liner and they all
 * render the identical Like · Comment · Share bar — no more per-surface re-wiring or
 * divergent copies.
 *
 *   - Auth-gated: an unauthenticated viewer gets NOTHING (returns null), so the
 *     card stays read-only with no like/share affordance or empty action region.
 *   - Per-post busy scoping is INTRINSIC: each card mounts its own `useLikePost`,
 *     so a like in flight disables ONLY that card's control.
 *   - NON-OPTIMISTIC: the mutation invalidates canonical Feed/detail caches; counts
 *     and liked_by_me move
 *     only to the refetched server value, never a fabricated local bump.
 *
 * ALL resharing (Repost, Repost with your thoughts, Send as message, Share to a
 * community) lives inside the share sheet (`PostShareDialog`), which owns its own
 * repost / DM mutations — so this wrapper no longer threads a repost mutation
 * through the bar; the bar just fires `onShare` to open the sheet.
 *
 * `hideComment` is forwarded to the bar — the post-DETAIL page passes it so the bar
 * renders Like + Share only (the comment thread + count live on that page). Every
 * feed surface omits it, so the Comment action stays (hideComment defaults false).
 */
export function PostActions({
  post,
  hideComment = false,
  commentSummary,
}: {
  post: PostResponse;
  hideComment?: boolean;
  commentSummary?: React.ReactNode;
}) {
  const { user } = useAuth();
  const like = useLikePost();
  // The share sheet's open state lives HERE (the smart wrapper), so the pure
  // PostActionBar stays presentational and only fires the onShare callback.
  const [shareOpen, setShareOpen] = useState(false);
  // Remember the invoking Share button so focus returns to it on close (WAI-ARIA
  // modal pattern — focus must not be stranded on the unmounted dialog). Captured
  // from document.activeElement at open time since the trigger lives inside the
  // presentational PostActionBar (no ref to thread up).
  const shareTriggerRef = useRef<HTMLElement | null>(null);

  const openShare = () => {
    shareTriggerRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    setShareOpen(true);
  };
  const closeShare = () => {
    setShareOpen(false);
    const trigger = shareTriggerRef.current;
    requestAnimationFrame(() => trigger?.focus());
  };

  // Auth gate: logged-out viewers see the card read-only (no actions rendered).
  if (!user) return null;

  return (
    <>
      <PostActionBar
        post={post}
        hideComment={hideComment}
        commentSummary={commentSummary}
        likeBusy={like.isPending}
        onLike={() =>
          like.mutate({
            postId: post.post_id,
            liked: Boolean(post.liked_by_me),
          })
        }
        onShare={openShare}
      />
      {/* Honest failure — a like that rejects surfaces an inline alert scoped to
          THIS card, and (being non-optimistic) the counts never moved. No
          fabricated success. Repost / share failures surface INSIDE the sheet. */}
      {like.isError ? (
        <GuardianAwareError error={like.error}>
          <Alert severity="error" role="alert" sx={{ mt: 1 }}>
            That didn&apos;t work. Please try again.
          </Alert>
        </GuardianAwareError>
      ) : null}
      {/* The share sheet. Mounted only while open (its repost / DM hooks fire only
          then), so a feed of cards never fans out a conversations fetch per card. */}
      <PostShareDialog post={post} open={shareOpen} onClose={closeShare} />
    </>
  );
}
